/**
 * Seating Plan Utility & Validation Engine
 * Implements full seating schema validation rules and unique ID generation.
 */

export function validatePlan(plan) {
  if (!plan || !plan.zones) return []

  const errors = []
  const seenZoneRowSeat = new Map()
  const seenGuids = new Map()

  plan.zones.forEach((zone) => {
    const zName = zone.name || 'Main Floor'
    ;(zone.rows || []).forEach((row) => {
      const rNum = String(row.row_number || '')
      ;(row.seats || []).forEach((seat) => {
        const sNum = String(seat.seat_number || '')
        const guid = String(seat.seat_guid || '')

        // Check 1: Duplicate zone + row + seat_number combination
        const comboKey = `${zName}:::${rNum}:::${sNum}`
        if (seenZoneRowSeat.has(comboKey)) {
          errors.push(
            `You have multiple seats with zone "${zName}", row "${rNum}", and seat "${sNum}". This is going to be very confusing.`
          )
        } else {
          seenZoneRowSeat.set(comboKey, true)
        }

        // Check 2: Duplicate seat_guid (Seat ID)
        if (guid) {
          if (seenGuids.has(guid)) {
            const firstLocation = seenGuids.get(guid)
            errors.push(
              `Seat ID "${guid}" is not unique! This will lead to errors when people try to book these seats. (Seen again in "${firstLocation.zName}" > "${firstLocation.rNum}" > "${firstLocation.sNum}")`
            )
          } else {
            seenGuids.set(guid, { zName, rNum, sNum })
          }
        }
      })
    })
  })

  return errors
}

/**
 * Generate a guaranteed unique seat GUID across the entire plan
 */
export function generateUniqueSeatGuid(plan, zonePrefix, rowNumber, seatNumber) {
  const cleanPrefix = (zonePrefix || 'MainFloor').replace(/\s+/g, '')
  const base = `${cleanPrefix}-${rowNumber}-${seatNumber}`

  // Gather all existing GUIDs in the plan
  const existing = new Set()
  if (plan && plan.zones) {
    plan.zones.forEach((z) => {
      ;(z.rows || []).forEach((r) => {
        ;(r.seats || []).forEach((s) => {
          if (s.seat_guid) existing.add(s.seat_guid)
        })
      })
    })
  }

  if (!existing.has(base)) {
    return base
  }

  let counter = 1
  while (existing.has(`${base}-${counter}`)) {
    counter++
  }
  return `${base}-${counter}`
}

/**
 * Auto-fix any duplicate seat IDs in an imported or modified plan
 */
export function deduplicateSeatGuids(plan) {
  if (!plan || !plan.zones) return plan

  const seen = new Set()
  const updatedZones = plan.zones.map((zone) => {
    const rawPrefix = zone.id_for_seat_ids || zone.name || 'MainFloor'
    const zonePrefix = (rawPrefix === 'Groundfloor' ? 'MainFloor' : rawPrefix).replace(/\s+/g, '')
    return {
      ...zone,
      rows: (zone.rows || []).map((row) => {
        const effectiveRow = (row.row_label && String(row.row_label).trim() !== '')
          ? String(row.row_label).trim()
          : String(row.row_number || '1')

        return {
          ...row,
          seats: (row.seats || []).map((seat) => {
            let guid = `${zonePrefix}-${effectiveRow}-${seat.seat_number}`
            if (seen.has(guid)) {
              let counter = 1
              while (seen.has(`${guid}-${counter}`)) {
                counter++
              }
              guid = `${guid}-${counter}`
            }
            seen.add(guid)
            return {
              ...seat,
              seat_guid: guid,
            }
          }),
        }
      }),
    }
  })

  return {
    ...plan,
    zones: updatedZones,
  }
}

/**
 * Auto-resolve duplicate row numbers across the plan by assigning distinct sequential row numbers
 * and updating all child seat GUIDs. Resolves duplicate (zone, row, seat) conflicts instantly.
 */
export function autoResolveDuplicateRowNumbers(plan) {
  if (!plan || !plan.zones) return plan

  const updatedZones = plan.zones.map((zone) => {
    const rawPrefix = zone.id_for_seat_ids || zone.name || 'MainFloor'
    const zonePrefix = (rawPrefix === 'Groundfloor' ? 'MainFloor' : rawPrefix).replace(/\s+/g, '')
    const seenRowNumbers = new Set()
    let nextRowCounter = 1

    const updatedRows = (zone.rows || []).map((row, rIdx) => {
      let rNum = String(row.row_number || (rIdx + 1))
      if (seenRowNumbers.has(rNum)) {
        while (seenRowNumbers.has(String(nextRowCounter))) {
          nextRowCounter++
        }
        rNum = String(nextRowCounter)
        nextRowCounter++
      }
      seenRowNumbers.add(rNum)

      const updatedSeats = (row.seats || []).map((seat) => ({
        ...seat,
        seat_guid: `${zonePrefix}-${rNum}-${seat.seat_number}`,
      }))

      return {
        ...row,
        row_number: rNum,
        row_label:
          row.row_label !== undefined
            ? row.row_label
            : `Row ${rNum}`,
        seats: updatedSeats,
      }
    })

    return {
      ...zone,
      rows: updatedRows,
    }
  })

  return {
    ...plan,
    zones: updatedZones,
  }
}

/**
 * Generate seat number label based on pattern, start number, reversed flag, index, and total seats
 */
export function generateSeatNum(pattern, start, isReversed, idx, total) {
  if (pattern === '25, 24, 23, …') {
    const base = Number(start) || total
    return String(isReversed ? base - (total - 1 - idx) : base - idx)
  }
  if (pattern === 'A, B, C, …') {
    const startCharCode = (start && isNaN(Number(start)))
      ? start.toString().toUpperCase().charCodeAt(0)
      : 65 + (Number(start) - 1)
    const code = isReversed
      ? startCharCode + (total - 1 - idx)
      : startCharCode + idx
    return String.fromCharCode(Math.min(code, 90))
  }
  // Default numeric ("1, 2, 3, …")
  const base = Number(start) || 1
  return String(isReversed ? base + (total - 1 - idx) : base + idx)
}

/**
 * Calculate row seat local positions and seat rotation based on seat count, spacing, and curve angle.
 * Supports existing seat positions so custom gaps/aisles (e.g. created with tool 'S') are preserved along the curve.
 */
export function calculateRowSeatPositions(
  seatCount,
  seatSpacing = 25,
  curveAngle = 0,
  existingSeats = null
) {
  const N = Math.max(1, seatCount)
  if (N <= 1) {
    return [{ x: 0, y: 0, rotation: 0 }]
  }
  const S = Number(seatSpacing) || 25

  // Calculate cumulative baseline distances for each seat along the row
  let cumDist = null
  if (Array.isArray(existingSeats) && existingSeats.length >= 2) {
    const rawSeats = existingSeats.slice(0, N)
    const hasPositions = rawSeats.every((s) => s?.position?.x !== undefined)
    if (hasPositions) {
      cumDist = [0]
      for (let i = 1; i < rawSeats.length; i++) {
        const pPrev = rawSeats[i - 1].position
        const pCurr = rawSeats[i].position
        const dx = (pCurr.x || 0) - (pPrev.x || 0)
        const dy = (pCurr.y || 0) - (pPrev.y || 0)
        const stepDist = Math.hypot(dx, dy)
        // If step distance is reasonable (> 1px), preserve custom spacing/gap; else fallback to S
        cumDist.push(cumDist[i - 1] + (stepDist > 1 ? stepDist : S))
      }
      // If seatCount > existingSeats.length (e.g. adding a new seat at the end)
      while (cumDist.length < N) {
        cumDist.push(cumDist[cumDist.length - 1] + S)
      }
    }
  }

  // Fallback to standard uniform grid if no existing seats or mismatch
  if (!cumDist || cumDist.length !== N) {
    cumDist = Array.from({ length: N }, (_, i) => i * S)
  }

  const L = cumDist[N - 1] || ((N - 1) * S)

  if (!curveAngle || Math.abs(Number(curveAngle)) < 0.1) {
    return Array.from({ length: N }, (_, i) => ({
      x: Math.round(cumDist[i] * 10) / 10,
      y: 0,
      rotation: 0,
    }))
  }

  const angleDeg = Number(curveAngle)
  const sign = angleDeg < 0 ? -1 : 1
  const absDeg = Math.abs(angleDeg)

  // Full circle (360° or -360°): distribute seats around loop preserving gaps
  if (absDeg >= 359.5) {
    const circlePerimeter = L + S
    const R = circlePerimeter / (2 * Math.PI)
    return Array.from({ length: N }, (_, i) => {
      const t = (cumDist[i] - L / 2) / circlePerimeter
      const phi = sign * t * 2 * Math.PI
      const x = circlePerimeter / 2 + R * Math.sin(phi)
      const y = R * (1 - Math.cos(phi))
      const rotDeg = Math.round((phi * 180) / Math.PI)
      return {
        x: Math.round(x * 10) / 10,
        y: Math.round(y * 10) / 10,
        rotation: rotDeg,
      }
    })
  }

  const alpha = (angleDeg * Math.PI) / 180
  const R = L / alpha

  return Array.from({ length: N }, (_, i) => {
    // t ranges from -0.5 to +0.5 proportionally along cumulative baseline length L
    const t = L > 0 ? (cumDist[i] / L - 0.5) : (i / (N - 1) - 0.5)
    const phi = t * alpha
    const x = L / 2 + R * Math.sin(phi)
    const y = R * (1 - Math.cos(phi))
    const rotDeg = Math.round((phi * 180) / Math.PI)
    return {
      x: Math.round(x * 10) / 10,
      y: Math.round(y * 10) / 10,
      rotation: rotDeg,
    }
  })
}

/**
 * Calculate accurate positions for row labels next to the first and/or last seat of a row.
 * Handles straight, curved, tilted, and full-circle (360°) rows.
 */
export function calculateRowLabelPositions(row) {
  const seats = row.seats || []
  if (seats.length === 0) return []

  const showNumbers = row.show_row_numbers !== undefined ? Boolean(row.show_row_numbers) : true
  if (!showNumbers) return []

  const labelText = row.row_label !== undefined && row.row_label !== ""
    ? String(row.row_label)
    : String(row.row_number || "")
  if (!labelText) return []

  const curveAngle = Math.abs(Number(row.curve_angle || 0))
  const isFullCircle = curveAngle >= 359.5 || Boolean(row.is_circular)

  // Position preference: 'both' (default), 'left', 'right'
  const position = row.row_label_position || 'both'

  // If full circle (360°), only one label is rendered because the start and end meet
  if (isFullCircle) {
    const s0 = seats[0]
    const r0 = s0.radius || row.seat_radius || 10

    // Compute geometric centroid of the circular seats
    let circleCenterX = 0
    let circleCenterY = 0
    seats.forEach((s) => {
      circleCenterX += (s.position?.x ?? 0)
      circleCenterY += (s.position?.y ?? 0)
    })
    circleCenterX /= seats.length
    circleCenterY /= seats.length

    const dx = (s0.position?.x ?? 0) - circleCenterX
    const dy = (s0.position?.y ?? 0) - circleCenterY
    const dist = Math.hypot(dx, dy)
    const ux = dist > 0.001 ? dx / dist : 0
    const uy = dist > 0.001 ? dy / dist : -1
    const offset = r0 + 14

    return [
      {
        side: 'circle',
        text: labelText,
        x: (s0.position?.x ?? 0) + ux * offset,
        y: (s0.position?.y ?? 0) + uy * offset,
        textAnchor: 'middle',
        dominantBaseline: 'central',
      },
    ]
  }

  // Standard or Curved Rows (Angle < 360°)
  const labels = []
  const s0 = seats[0]
  const sLast = seats[seats.length - 1]
  const r0 = s0.radius || row.seat_radius || 10
  const rLast = sLast.radius || row.seat_radius || 10
  const offset0 = r0 + 14
  const offsetLast = rLast + 14

  // 1. First seat (Start / Left)
  if (position === 'both' || position === 'left') {
    let ux = -1
    let uy = 0
    if (seats.length >= 2) {
      const s1 = seats[1]
      const dx = (s0.position?.x ?? 0) - (s1.position?.x ?? 0)
      const dy = (s0.position?.y ?? 0) - (s1.position?.y ?? 0)
      const dist = Math.hypot(dx, dy)
      if (dist > 0.001) {
        ux = dx / dist
        uy = dy / dist
      }
    }
    labels.push({
      side: 'left',
      text: labelText,
      x: (s0.position?.x ?? 0) + ux * offset0,
      y: (s0.position?.y ?? 0) + uy * offset0,
      textAnchor: 'middle',
      dominantBaseline: 'central',
    })
  }

  // 2. Last seat (End / Right)
  if (position === 'both' || position === 'right') {
    let ux = 1
    let uy = 0
    if (seats.length >= 2) {
      const sPrev = seats[seats.length - 2]
      const dx = (sLast.position?.x ?? 0) - (sPrev.position?.x ?? 0)
      const dy = (sLast.position?.y ?? 0) - (sPrev.position?.y ?? 0)
      const dist = Math.hypot(dx, dy)
      if (dist > 0.001) {
        ux = dx / dist
        uy = dy / dist
      }
    }
    labels.push({
      side: 'right',
      text: labelText,
      x: (sLast.position?.x ?? 0) + ux * offsetLast,
      y: (sLast.position?.y ?? 0) + uy * offsetLast,
      textAnchor: 'middle',
      dominantBaseline: 'central',
    })
  }

  return labels
}

/**
 * Convert 0-based index to spreadsheet-style alphabetical label:
 * 0 -> "A", 25 -> "Z", 26 -> "AA", 27 -> "AB", ..., 51 -> "AZ", 52 -> "BA", etc.
 */
export function indexToAlphabet(idx) {
  let s = ''
  let n = Math.max(0, Math.floor(idx))
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s
    n = Math.floor(n / 26) - 1
  }
  return s
}

/**
 * Convert alphabetical string back to 0-based index:
 * "A" -> 0, "Z" -> 25, "AA" -> 26, "AB" -> 27, etc.
 */
export function alphabetToIndex(str) {
  if (!str) return 0
  const clean = String(str).toUpperCase().replace(/[^A-Z]/g, '')
  if (!clean) return 0
  let idx = 0
  for (let i = 0; i < clean.length; i++) {
    idx = idx * 26 + (clean.charCodeAt(i) - 64)
  }
  return Math.max(0, idx - 1)
}

/**
 * Compute absolute visual coordinate (absX, absY) of a seat taking into account
 * parent row position and rotation.
 */
export function getSeatVisualPosition(row, seat) {
  const rRad = ((row?.rotation || 0) * Math.PI) / 180
  const cosR = Math.cos(rRad)
  const sinR = Math.sin(rRad)
  const rx = row?.position?.x || 0
  const ry = row?.position?.y || 0
  const sx = seat?.position?.x || 0
  const sy = seat?.position?.y || 0
  return {
    x: rx + (sx * cosR - sy * sinR),
    y: ry + (sx * sinR + sy * cosR),
  }
}

/**
 * Compute geometric center (visual X and Y) of a row based on its seats.
 */
export function getRowVisualPosition(row) {
  const seats = row?.seats || []
  if (seats.length === 0) {
    return {
      x: row?.position?.x || 0,
      y: row?.position?.y || 0,
    }
  }
  let sumX = 0
  let sumY = 0
  seats.forEach((seat) => {
    const pos = getSeatVisualPosition(row, seat)
    sumX += pos.x
    sumY += pos.y
  })
  return {
    x: sumX / seats.length,
    y: sumY / seats.length,
  }
}

/**
 * Sort a list of rows strictly from top to bottom based on actual visual position.
 * If two rows have virtually identical visual Y (within 5px), sort left to right.
 * The input is an array of objects containing at least { row, rowIndex }.
 */
export function sortRowsTopToBottom(rowsWithMetadata) {
  return [...rowsWithMetadata].sort((a, b) => {
    const posA = getRowVisualPosition(a.row)
    const posB = getRowVisualPosition(b.row)
    // Primary: top to bottom (smaller Y comes first)
    if (Math.abs(posA.y - posB.y) > 5) {
      return posA.y - posB.y
    }
    // Secondary: left to right (smaller X comes first)
    return posA.x - posB.x
  })
}

/**
 * Sort seats inside a row spatially:
 * By default: Left to right (smaller visual X comes first, or smaller visual Y if nearly vertical).
 * If direction === 'rtl': Right to left.
 */
export function sortRowSeatsSpatially(row, direction = 'ltr') {
  const seats = (row?.seats || []).map((seat, originalSeatIdx) => ({
    seat,
    originalSeatIdx,
    pos: getSeatVisualPosition(row, seat),
  }))

  seats.sort((a, b) => {
    // If nearly vertical row (X diff < 4px), sort top to bottom
    if (Math.abs(a.pos.x - b.pos.x) < 4) {
      return a.pos.y - b.pos.y
    }
    return a.pos.x - b.pos.x
  })

  if (direction === 'rtl') {
    seats.reverse()
  }

  return seats
}

/**
 * Remove empty rows (rows with 0 seats) from all zones.
 */
export function purgeEmptyRows(plan) {
  if (!plan || !plan.zones) return plan
  return {
    ...plan,
    zones: plan.zones.map((zone) => ({
      ...zone,
      rows: (zone.rows || []).filter((r) => (r.seats || []).length > 0),
    })),
  }
}

/**
 * Calculate preview data for auto-labeling rows and numbering seats without modifying state.
 */
export function calculateAutoLabelsPreview({
  rowsWithMetadata, // Array of { row, rowIndex }
  rowLabelType = 'alphabetical', // 'alphabetical', 'numeric', 'none'
  startRowLabel = 'A', // 'A' or '1'
  seatNumberMode = 'continuous', // 'continuous', 'range', 'restart', 'none'
  startSeatNumber = 1, // Number
  endSeatNumber = 50, // Number (for 'range' mode)
  seatDirection = 'ltr', // 'ltr', 'rtl'
  excludeEmptyRows = true,
}) {
  // Exclude empty rows (0 seats) so they do NOT consume letters (A, B, C...) or skew numbering
  const filtered = excludeEmptyRows
    ? (rowsWithMetadata || []).filter((item) => (item.row?.seats || []).length > 0)
    : (rowsWithMetadata || [])

  // 1. Sort rows top to bottom based on actual visual coordinates
  const sortedRows = sortRowsTopToBottom(filtered)

  let currentSeatNumber = Number(startSeatNumber) || 1
  const startAlphabetIdx = alphabetToIndex(startRowLabel)
  const startRowNumeric = Number(startRowLabel) || 1

  const rangeStart = Math.max(1, Number(startSeatNumber) || 1)
  const rangeEnd = Math.max(rangeStart, Number(endSeatNumber) || (rangeStart + 49))
  let currentRangeNumber = rangeStart

  return sortedRows.map((item, sortedIdx) => {
    const row = item.row
    const rowIndex = item.rowIndex
    const oldRowLabel = row.row_label !== undefined && row.row_label !== ''
      ? String(row.row_label)
      : String(row.row_number || '')

    // Determine new row label
    let newRowLabel = oldRowLabel
    if (rowLabelType === 'alphabetical') {
      newRowLabel = indexToAlphabet(startAlphabetIdx + sortedIdx)
    } else if (rowLabelType === 'numeric') {
      newRowLabel = String(startRowNumeric + sortedIdx)
    }

    // Determine seat numbering for this row
    const sortedSeats = sortRowSeatsSpatially(row, seatDirection)
    const seatCount = sortedSeats.length
    let seatStart = null
    let seatEnd = null

    if (seatNumberMode === 'restart') {
      currentSeatNumber = Number(startSeatNumber) || 1
    }

    const previewSeats = sortedSeats.map((sItem, sIdx) => {
      const oldSeatNumber = String(sItem.seat.seat_number || '')
      let newSeatNumber = oldSeatNumber

      if (seatNumberMode === 'range') {
        newSeatNumber = String(currentRangeNumber)
        if (currentRangeNumber >= rangeEnd) {
          currentRangeNumber = rangeStart
        } else {
          currentRangeNumber += 1
        }
      } else if (seatNumberMode === 'restart') {
        newSeatNumber = String(currentSeatNumber + sIdx)
      } else if (seatNumberMode === 'continuous') {
        newSeatNumber = String(currentSeatNumber + sIdx)
      }

      return {
        seat: sItem.seat,
        originalSeatIdx: sItem.originalSeatIdx,
        oldSeatNumber,
        newSeatNumber,
      }
    })

    if (seatNumberMode === 'continuous' && seatCount > 0) {
      seatStart = currentSeatNumber
      seatEnd = currentSeatNumber + seatCount - 1
      currentSeatNumber += seatCount
    } else if (seatNumberMode === 'restart' && seatCount > 0) {
      seatStart = Number(startSeatNumber) || 1
      seatEnd = (Number(startSeatNumber) || 1) + seatCount - 1
    }

    let seatRangeText = '-'
    if (seatCount > 0) {
      if (seatNumberMode === 'range') {
        // Group contiguous sequence segments cleanly (e.g. 1–10 or 45–50, 1–4)
        const seatNums = previewSeats.map((s) => Number(s.newSeatNumber))
        const segments = []
        let segStart = seatNums[0]
        let prev = seatNums[0]
        for (let i = 1; i < seatNums.length; i++) {
          if (seatNums[i] === prev + 1) {
            prev = seatNums[i]
          } else {
            segments.push(segStart === prev ? `${segStart}` : `${segStart}–${prev}`)
            segStart = seatNums[i]
            prev = seatNums[i]
          }
        }
        segments.push(segStart === prev ? `${segStart}` : `${segStart}–${prev}`)
        seatRangeText = segments.join(', ')
      } else if (seatNumberMode === 'continuous' || seatNumberMode === 'restart') {
        seatRangeText = `${seatStart}–${seatEnd}`
      } else {
        const firstNum = previewSeats[0]?.oldSeatNumber
        const lastNum = previewSeats[previewSeats.length - 1]?.oldSeatNumber
        seatRangeText = firstNum && lastNum ? `${firstNum}–${lastNum}` : `${seatCount} seats`
      }
    }

    return {
      rowIndex,
      row,
      oldRowLabel,
      newRowLabel,
      seatCount,
      seatStart,
      seatEnd,
      seatRangeText,
      seats: previewSeats,
    }
  })
}

/**
 * Apply auto row labeling and seat numbering safely to a plan.
 * Preserves all UUIDs, spatial positions, dimensions, categories, and geometry.
 */
export function applyAutoLabelsToPlan({
  plan,
  activeZoneIndex = 0,
  rowsWithMetadata,
  rowLabelType = 'alphabetical',
  startRowLabel = 'A',
  seatNumberMode = 'continuous',
  startSeatNumber = 1,
  endSeatNumber = 50,
  seatDirection = 'ltr',
}) {
  if (!plan || !plan.zones || !plan.zones[activeZoneIndex]) return plan

  const currentZone = plan.zones[activeZoneIndex]
  const zonePrefix = (
    currentZone.id_for_seat_ids ||
    currentZone.name ||
    'MainFloor'
  ).replace(/\s+/g, '')

  const preview = calculateAutoLabelsPreview({
    rowsWithMetadata,
    rowLabelType,
    startRowLabel,
    seatNumberMode,
    startSeatNumber,
    endSeatNumber,
    seatDirection,
  })

  // Map each rowIndex to its computed new data
  const updatesByRowIndex = new Map()
  preview.forEach((p) => {
    updatesByRowIndex.set(p.rowIndex, p)
  })

  // Map and remove any empty rows with 0 seats
  const nextRows = (currentZone.rows || [])
    .map((row, rIdx) => {
      if (!updatesByRowIndex.has(rIdx)) {
        return row
      }

      const p = updatesByRowIndex.get(rIdx)
      const newRowLabel = p.newRowLabel

      // Map each original seat index to its new seat number
      const seatUpdatesByOrigIdx = new Map()
      p.seats.forEach((s) => {
        seatUpdatesByOrigIdx.set(s.originalSeatIdx, s.newSeatNumber)
      })

      const updatedSeats = (row.seats || []).map((seat, sIdx) => {
        const newSeatNum = seatUpdatesByOrigIdx.has(sIdx)
          ? seatUpdatesByOrigIdx.get(sIdx)
          : seat.seat_number

        const effectiveRowId = rowLabelType !== 'none' ? newRowLabel : (row.row_number || String(rIdx + 1))
        const newGuid = `${zonePrefix}-${effectiveRowId}-${newSeatNum}`

        return {
          ...seat,
          seat_number: String(newSeatNum),
          seat_guid: newGuid,
        }
      })

      return {
        ...row,
        row_label: rowLabelType !== 'none' ? newRowLabel : row.row_label,
        row_number: rowLabelType !== 'none' ? newRowLabel : row.row_number,
        show_row_numbers: true,
        seats: updatedSeats,
      }
    })
    .filter((row) => (row.seats || []).length > 0)

  const updatedZones = [...plan.zones]
  updatedZones[activeZoneIndex] = {
    ...currentZone,
    rows: nextRows,
  }

  // Deduplicate any potential GUID conflicts safely
  const updatedPlan = deduplicateSeatGuids({
    ...plan,
    zones: updatedZones,
  })

  return updatedPlan
}

